import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ComicCard from "@/components/feature/ComicCard";
import { comics, categories } from "@/mocks/comics";

const sortOptions = [
  { key: "hot", label: "按热度" },
  { key: "rating", label: "按评分" },
  { key: "new", label: "按更新" },
];

export default function Category() {
  const { slug = "all" } = useParams();
  const [params, setParams] = useSearchParams();

  const initStatus = params.get("status") === "finished" ? "已完结" : "all";
  const [sort, setSort] = useState("hot");
  const [status, setStatus] = useState(initStatus);
  const searchQ = params.get("q") || "";

  const current = categories.find((c) => c.slug === slug);
  const pageName = current ? `${current.name}漫画` : "全部漫画";
  const categoryKey = slug === "all" ? null : slug;

  const list = useMemo(() => {
    let arr = categoryKey ? comics.filter((c) => c.category === categoryKey) : [...comics];
    if (status !== "all") arr = arr.filter((c) => c.status === status);
    if (searchQ.trim()) {
      const kw = searchQ.trim().toLowerCase();
      arr = arr.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw)) ||
          c.description.toLowerCase().includes(kw)
      );
    }
    if (sort === "rating") arr = [...arr].sort((a, b) => b.rating - a.rating);
    else if (sort === "new") arr = [...arr].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    else arr = [...arr].sort((a, b) => b.views - a.views);
    return arr;
  }, [categoryKey, searchQ, sort, status]);

  const updateQ = (v: string) => {
    if (v.trim()) {
      const next = new URLSearchParams(params);
      next.set("q", v.trim());
      setParams(next, { replace: true });
    } else {
      const next = new URLSearchParams(params);
      next.delete("q");
      setParams(next, { replace: true });
    }
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <Seo
        title={`${pageName}合集 | 虚空漫画网 ${current ? current.desc : "异世界/穿越/奇幻/脑洞/玄幻全集"}热门推荐`}
        description={current ? `虚空漫画网${pageName}精选，${current.desc}` : "虚空漫画网聚合异世界、穿越、奇幻、脑洞、玄幻题材漫画，热门榜单与完本推荐每日更新。"}
        path={`/category/${slug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://talespots.github.io" },
              { "@type": "ListItem", position: 2, name: pageName, item: `https://talespots.github.io/category/${slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${pageName}合集`,
            description: current ? current.desc : "虚空漫画网全题材漫画合集",
            url: `https://talespots.github.io/category/${slug}`,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: list.slice(0, 12).map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.title,
                url: `https://talespots.github.io/comic/${c.id}`,
              })),
            },
          },
        ]}
      />

      {/* 分类头图 */}
      <section className="relative flex min-h-[240px] items-center overflow-hidden md:min-h-[300px]">
        <div className="absolute inset-0">
          <img
            src={current ? current.image : "https://readdy.ai/api/search-image?query=wide%20dark%20fantasy%20manga%20collage%20banner%20with%20warm%20amber%20and%20crimson%20glowing%20energy%20swords%20books%20and%20dragons%20silhouettes%2C%20cinematic%20atmosphere%2C%20no%20text&width=1600&height=500&seq=cat-all-2026&orientation=landscape"}
            alt={`${pageName}横幅`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/40" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-300">{pageName}</span>
          </nav>
          <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-5xl">{pageName}合集</h1>
          <p className="mt-3 max-w-xl text-sm leading-6 text-foreground-600 md:text-base">{current ? current.desc : "聚合五大题材，热门榜单与完本推荐每日更新，为你找到下一部入坑作。"}</p>
          <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-background-950/50 px-4 py-1.5 text-xs font-medium text-primary-300">
            <i className="ri-archive-drawer-line" />
            收录 {categoryKey ? comics.filter((c) => c.category === categoryKey).length : comics.length} 部精选
          </span>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {/* 筛选工具条 */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/category/all" className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${!categoryKey ? "bg-primary-500 text-background-950" : "border border-background-700 text-foreground-600 hover:border-primary-500/50 hover:text-primary-300"}`}>
              全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${categoryKey === c.slug ? "bg-primary-500 text-background-950" : "border border-background-700 text-foreground-600 hover:border-primary-500/50 hover:text-primary-300"}`}
              >
                {c.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 rounded-xl border border-background-800 bg-background-900 p-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-full bg-background-950 p-1">
                {sortOptions.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setSort(o.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${sort === o.key ? "bg-primary-500 text-background-950" : "text-foreground-600 hover:text-foreground-950"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="flex rounded-full bg-background-950 p-1">
                {[
                  { key: "all", label: "全部状态" },
                  { key: "连载中", label: "连载中" },
                  { key: "已完结", label: "已完结" },
                ].map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setStatus(o.key)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${status === o.key ? "bg-accent-500 text-background-950" : "text-foreground-600 hover:text-foreground-950"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative md:w-72">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
              <input
                type="text"
                defaultValue={searchQ}
                onChange={(e) => updateQ(e.target.value)}
                placeholder="在当前分类中搜索"
                aria-label="分类内搜索"
                className="w-full rounded-lg border border-background-700 bg-background-950 py-2.5 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="mt-8">
          {list.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {list.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-background-800 bg-background-900 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-800 text-2xl text-foreground-500">
                <i className="ri-inbox-2-line" />
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到匹配的漫画</p>
              <p className="mt-2 text-sm text-foreground-500">换个关键词或分类试试吧</p>
              <button
                onClick={() => {
                  updateQ("");
                  setStatus("all");
                }}
                className="mt-6 cursor-pointer whitespace-nowrap rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}